<template>
    <div class="rates-info d-flex justify-content-lg-start">
        <i class="icon-check-shield d-flex"></i>

        <div
            class="d-flex flex-wrap justify-content-center justify-content-lg-start rate-info-text"
            :class="[approvalText.length > 22 ? 'smaller-info' : '']"
        >
            <span class="mr-1">
                {{ approvalText }}
            </span>

            <span class="light-text d-none d-md-flex mr-1" v-if="showRateGuaranteeText">
                • {{ rateGuaranteeText }}
            </span>

            <span class="light-text d-flex d-md-none mr-1" v-if="showMobileRateGuaranteeText">
                • {{ mobileRateGuaranteeText }}
            </span>
        </div>
    </div>
</template>

<script>
export default {
    data() {
        return {
            tooltipOptions: {
                content: null,
                trigger: 'click',
                arrow: true,
            },
        }
    },
    props: ['offer', 'isAlternativeOffer', 'isDebtSolutionOffer'],
    computed: {
        territory: function () {
            return process.env.TERRITORY
        },
        type: function () {
            let type = this.offer.group

            if (this.offer.type === 'car_finance') {
                type = 'car'
            } else if (this.offer.secured) {
                type = 'secured'
            }

            return type
        },
        approvalText: function () {
            const offerLikelihood = this.offer.likelihood

            if (this.offer.type === 'alternative') {
                return this.$t('results.offerBlocks.approvalText.alternative')
            }

            if (this.offer.type === 'debt_solution' && this.territory === 'gbr') {
                return this.$t('results.offerBlocks.approvalText.debtSolution', {
                    lenderName: this.offer?.lender?.name,
                })
            }

            if (this.offer.pre_approved === true && this.includeTerritories('usa')) {
                return this.$t('results.offerBlocks.approvalText.preApproved')
            }

            if (
                this.offer.pre_approved === false &&
                this.offer.pre_qualified === true &&
                this.includeTerritories('usa')
            ) {
                return this.$t('results.offerBlocks.approvalText.preQualify')
            }

            if (
                (this.offer.lender.rate_type === 'Confirmed_APR' && offerLikelihood === '100') ||
                (this.offer.lender.rate_type === 'Rep_APR' && offerLikelihood === '100') ||
                this.type === 'prequal_same_loan_amount' ||
                this.type === 'prequal_different_loan_amount'
            ) {
                if (this.includeTerritories('usa')) {
                    return this.$t('results.offerBlocks.approvalText.preQualify')
                }
                return this.$t('results.offerBlocks.approvalText.preApproved')
            }

            if (this.includeTerritories('aus', 'pol')) {
                return this.$t('results.offerBlocks.approvalText.alternative')
            }

            switch (this.type) {
                case 'prequal_same_loan_amount':
                case 'prequal_different_loan_amount': {
                    const likelihood = offerLikelihood ? `${offerLikelihood}%` : '100%'
                    return `${likelihood} ${this.$t('results.offerBlocks.approvalText.chanceOfApproval')}`
                }
                default: {
                    if (offerLikelihood && parseInt(offerLikelihood) === 100 && this.includeTerritories('gbr')) {
                        return this.$t('results.offerBlocks.approvalText.preApproved')
                    } else if (offerLikelihood && parseInt(offerLikelihood) !== 0) {
                        return `${offerLikelihood}% ${this.$t('results.offerBlocks.approvalText.chanceOfApproval')}`
                    } else {
                        return this.$t('results.offerBlocks.approvalText.alternative')
                    }
                }
            }
        },
        rateGuaranteeText: function () {
            if (this.offer.apr_prefix === 'Confirmed' || this.offer.lender.rate_type === 'Confirmed_APR') {
                return this.$t('results.offerBlocks.rateGuaranteeText.guaranteed')
            }

            if (
                this.offer.apr_prefix === 'Rep' ||
                this.offer.apr_prefix === 'Aprc' ||
                this.offer.lender.rate_type === 'Rep_APR'
            ) {
                return this.$t('results.offerBlocks.rateGuaranteeText.notGuaranteed')
            }

            return false
        },
        mobileRateGuaranteeText: function () {
            if (this.offer.apr_prefix === 'Confirmed' || this.offer.lender.rate_type === 'Confirmed_APR') {
                return this.$t('results.offerBlocks.rateGuaranteeText.guaranteedMobile')
            }

            if (
                this.offer.apr_prefix === 'Rep' ||
                this.offer.apr_prefix === 'Aprc' ||
                this.offer.lender.rate_type === 'Rep_APR'
            ) {
                return this.$t('results.offerBlocks.rateGuaranteeText.notGuaranteedMobile')
            }

            return false
        },
        showRateGuaranteeText: function () {
            return this.rateGuaranteeText && !this.isAlternativeOffer && !this.isDebtSolutionOffer
        },
        showMobileRateGuaranteeText: function () {
            return this.mobileRateGuaranteeText && !this.isAlternativeOffer && !this.isDebtSolutionOffer
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';
@import 'v-tooltip/dist/v-tooltip.css';

.icon-question-mark {
    height: 22px;
    margin-left: 15px;
    min-width: 15px;
    position: relative;

    @media screen and (min-width: $bp-medium) {
        min-width: 18px;
    }

    &:before {
        content: var(--url-question-mark-circle);
        height: 100%;
        position: absolute;
        top: 2px;
        width: 100%;
    }
}

.icon-check-shield {
    height: 100%;
    margin-right: 5px;
    min-width: 15px;
    position: relative;

    @media screen and (min-width: $bp-medium) {
        min-width: 18px;
        margin-right: 15px;
    }

    &:before {
        content: var(--url-check-shield);
        left: 50%;
        position: absolute;
        top: calc(50% + 2px);
        transform: translate(-50%, -50%);
        width: 100%;
    }
}

.rates-info {
    border-radius: 30px;
    border: 1px solid #e5e5e5;
    color: #808080;
    font-family: var(--primary-font);
    font-size: rem(14px);
    font-weight: 600;
    margin-bottom: 12px;
    padding: 10px;
    width: 100%;

    @media screen and (min-width: $bp-large) {
        width: initial;
    }

    .smaller-info {
        span {
            font-size: rem(13px);

            @media screen and (min-width: $bp-medium) {
                font-size: rem(14px);
            }
        }
    }

    span {
        color: black;
        font-size: rem(12px);
        font-weight: 600;
        text-align: center;

        @media screen and (max-width: $bp-small) {
            font-size: rem(14px);
        }

        @media screen and (min-width: $bp-medium) {
            font-size: rem(14px);
        }

        &.light-text {
            color: #808080;
        }
    }

    .caveat-text {
        color: var(--primary);
        font-size: rem(14px);
        font-weight: 600;
    }

    .rate-info-text {
        width: 100%;
    }
}
</style>

<style lang="scss">
@import '~scss/mixins/rem';
@import '~scss/settings/responsive';

.v-popper__wrapper {
    max-width: 900px;

    .v-popper__inner {
        font-size: rem(13px);

        div {
            color: white;
        }
    }
}
</style>
